import { useEffect, useMemo, useState } from 'react';

// Define public routes in a central place
const PUBLIC_ROUTES = [
  '/',               // home page
  '/help',
  '/search',
  '/terms',
  '/privacy',
  '/itineraries/create',
  '/my-itineraries', // public but shows sign-in prompt when not authenticated
  '/profile'         // public but shows sign-in prompt when not authenticated
] as string[];

// Type for matching route patterns
type RoutePattern = (path: string) => boolean;

// Define patterns for public routes (e.g., wildcards)
const PUBLIC_PATTERNS: RoutePattern[] = [
  // Example: Match all paths under /public/*
//   (path: string) => path.startsWith('/public/'),
  // Match /itineraries/{id} but not /itineraries/{id}/edit or other sub-paths
  (path: string) => {
    const itineraryPattern = /^\/itineraries\/[^\/]+\/?$/;
    return itineraryPattern.test(path);
  },
  // Match /itineraries/{itineraryId}/stops/{stopId} for public stop viewing
  (path: string) => {
    const stopPattern = /^\/itineraries\/[^\/]+\/stops\/[^\/]+\/?$/;
    return stopPattern.test(path);
  },
  // Match /itineraries/reels/{id} for public reel viewing
  (path: string) => {
    const reelPattern = /^\/itineraries\/reels\/[^\/]+\/?$/;
    return reelPattern.test(path);
  },
  (path: string) => {
    const publicProfilePattern = /^\/users\/[^\/]+\/?$/;
    return publicProfilePattern.test(path);
  }
];

export const useRouteAuth = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Create a MutationObserver to watch for URL changes that don't trigger popstate
    const observer = new MutationObserver(() => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    });

    // Observe URL changes
    observer.observe(document, { subtree: true, childList: true });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [currentPath]);

  const isPublicRoute = useMemo((): boolean => {
    console.debug('Checking auth for path:', currentPath);

    // Check exact matches
    if (PUBLIC_ROUTES.includes(currentPath as any)) {
      return true;
    }

    // Check patterns
    return PUBLIC_PATTERNS.some(pattern => pattern(currentPath));
  }, [currentPath]);

  return {
    requiresAuth: !isPublicRoute,
    isPublicRoute
  };
}; 